import { isNull, isUndefined } from 'lodash';
import { i18nService } from '@zonofi/i18n';

/**
 * Thin wrappers around `i18nService` for standardized currency formatting.
 * Pass `isMajor = true` for GSP service APIs (Invoice, Payments).
 */

/**
 * getFormattedAmount(amt?, isMajor = false): string
 *
 * Displays full amount with currency symbol. Example: "₹1,200.00"
 * Returns currency symbol only if amt is invalid.
 */
export const getFormattedAmount = (
  amt?: number | null,
  isMajor = false
): string => {
  if (!isUndefined(amt) && !isNull(amt) && isFinite(amt)) {
    return isMajor
      ? i18nService.formatMajorCurrency(amt)
      : i18nService.formatCurrency(amt);
  }
  return i18nService.getCurrencySymbol();
};

/**
 * getCalculatedAmount(val?, isMajor = false): string
 *
 * Displays compact amount (K, M, etc.) with currency symbol. Example: "₹1.2K"
 * Returns currency symbol only if val is invalid.
 */
export const getCalculatedAmount = (
  val?: string | number | null,
  isMajor = false
): string => {
  if (!isUndefined(val) && !isNull(val) && isFinite(Number(val))) {
    const amount = parseFloat(val.toString());
    return isMajor
      ? i18nService.formatCompactMajorCurrency(amount)
      : i18nService.formatCompactCurrency(amount);
  }
  return i18nService.getCurrencySymbol();
};

/**
 * getCurrencySign(): string
 *
 * Returns currency symbol for current locale. Example: "₹", "$", "€"
 * Ideal for text input prefixes.
 */
export const getCurrencySign = (): string => {
  return i18nService.getCurrencySymbol();
};

/**
 * toMinorCurrency(amt?): number
 *
 * Converts major to minor unit (e.g. rupees → paise).
 * Use for backend payloads. Returns 0 if invalid.
 */
export const toMinorCurrency = (amt?: number | null): number => {
  if (!isUndefined(amt) && !isNull(amt) && isFinite(amt)) {
    return i18nService.convertToMinorCurrency(amt);
  }
  return 0;
};

/**
 * toMajorCurrency(amt?): number
 *
 * Converts minor to major unit (e.g. paise → rupees).
 * Use for displaying/prefilling backend data. Returns 0 if invalid.
 */
export const toMajorCurrency = (amt?: number | null): number => {
  if (!isUndefined(amt) && !isNull(amt) && isFinite(amt)) {
    return i18nService.convertToMajorCurrency(amt);
  }
  return 0;
};

/**
 * isValidCurrencyAmount(amt: string): boolean
 *
 * Validates user-entered currency value for current locale/currency.
 */
export const isValidCurrencyAmount = (amt: string): boolean => {
  return i18nService.isValidDecimalCurrency(amt);
};

/**
 * getAmountByCurrencyCode(amt: number, currencyCode: string): string
 *
 * Formats amount with currency symbol by currency code.
 * Maps INR to 'en-IN', others to 'en-US' locale.
 */
export const getAmountByCurrencyCode = (
  amt: number,
  currencyCode: string
): string => {
  const locale = currencyCode === 'INR' ? 'en-IN' : 'en-US';
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
  });
  return formatter.format(amt);
};

export const getMaxDecimalPlaces = (): number => {
  return i18nService.getMaxDecimalPlaces();
};
